import { useState } from "react";
import {
  InfiniteListBase,
  ShowBase,
  useListContext,
  useRefresh,
  useShowContext,
  useTranslate,
} from "ra-core";
import { Calculator } from "lucide-react";
import { EditButton } from "@/components/admin/edit-button";
import { ReferenceField } from "@/components/admin/reference-field";
import { DateField } from "@/components/admin/date-field";
import { TextField } from "@/components/admin/text-field";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { NoteCreate, NotesPanel } from "../notes";
import { LineItemsTable, QuickAddMaterial } from "./LineItemsTable";
import { FreightTable } from "./FreightTable";
import { CustomsTable } from "./CustomsTable";
import { DocumentsPanel } from "./DocumentsPanel";
import { LOAD_STATUS_CHOICES, LOAD_TYPE_CHOICES, SHIPMENT_METHOD_CHOICES, FREIGHT_TERMS_CHOICES, TERMS_CHOICES, PAYMENT_METHOD_CHOICES } from "./LoadInputs";
import { getSupabaseClient } from "../providers/supabase/supabase";

export const LoadShow = () => {
  return (
    <ShowBase>
      <LoadShowContent />
    </ShowBase>
  );
};

const ShowField = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div>
    <p className="text-sm font-medium text-muted-foreground mb-1">{label}</p>
    <div className="text-sm">{children}</div>
  </div>
);

const MaterialsContent = ({ loadId }: { loadId: number }) => {
  const { refetch } = useListContext();
  return (
    <div className="flex flex-col gap-6">
      <QuickAddMaterial loadId={loadId} onCreated={refetch} />
      <Separator />
      <LineItemsTable loadId={loadId} side="buy" title="Vendor / Buy Side" />
      <Separator />
      <LineItemsTable loadId={loadId} side="sell" title="Customer / Sell Side" />
    </div>
  );
};

const LoadShowContent = () => {
  const { record, isPending } = useShowContext();
  const translate = useTranslate();
  if (isPending || !record) return null;

  const statusLabel =
    LOAD_STATUS_CHOICES.find((s) => s.id === record.status)?.name ??
    record.status;

  return (
    <div className="mt-2 mb-12 flex flex-col gap-6 w-full">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Load #{record.load_number}</h1>
          <Badge variant="outline">{statusLabel}</Badge>
        </div>
        <EditButton />
      </div>

      <Card>
        <CardContent className="pt-4">
          <h2 className="text-base font-semibold mb-4">Overview</h2>
          <Separator className="mb-4" />
          <div className="grid grid-cols-3 gap-6">
            <ShowField label={translate("resources.loads.fields.status")}>
              {statusLabel}
            </ShowField>
            <ShowField label={translate("resources.loads.fields.load_type")}>
              {LOAD_TYPE_CHOICES.find(c => c.id === record.load_type)?.name ?? record.load_type ?? "—"}
            </ShowField>
            <ShowField label="Main Commodity">
              {record.main_commodity || "—"}
            </ShowField>
            <ShowField label={translate("resources.loads.fields.vendor_reference_number")}>
              {record.vendor_reference_number || "—"}
            </ShowField>
            <ShowField label={translate("resources.loads.fields.customer_reference_number")}>
              {record.customer_reference_number || "—"}
            </ShowField>
            <ShowField label={translate("resources.loads.fields.pickup_date")}>
              <DateField source="pickup_date" />
            </ShowField>

            {record.hold_reason && (
              <div className="col-span-3">
                <ShowField label={translate("resources.loads.fields.hold_reason")}>
                  {record.hold_reason}
                </ShowField>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <h2 className="text-base font-semibold mb-4">Companies</h2>
          <Separator className="mb-4" />
          <div className="grid grid-cols-2 gap-6">
            <ShowField label={translate("resources.loads.fields.vendor_company_id")}>
              <ReferenceField source="vendor_company_id" reference="companies" link="show">
                <TextField source="name" />
              </ReferenceField>
            </ShowField>
            <ShowField label={translate("resources.loads.fields.customer_company_id")}>
              <ReferenceField source="customer_company_id" reference="companies" link="show">
                <TextField source="name" />
              </ReferenceField>
            </ShowField>
            <ShowField label={translate("resources.loads.fields.vendor_billing_company_id")}>
              <ReferenceField source="vendor_billing_company_id" reference="companies" link="show">
                <TextField source="name" />
              </ReferenceField>
            </ShowField>
            <ShowField label={translate("resources.loads.fields.customer_billing_company_id")}>
              <ReferenceField source="customer_billing_company_id" reference="companies" link="show">
                <TextField source="name" />
              </ReferenceField>
            </ShowField>
            <ShowField label={translate("resources.loads.fields.pickup_company_id")}>
              <ReferenceField source="pickup_company_id" reference="companies" link="show">
                <TextField source="name" />
              </ReferenceField>
            </ShowField>
            <ShowField label={translate("resources.loads.fields.delivery_company_id")}>
              <ReferenceField source="delivery_company_id" reference="companies" link="show">
                <TextField source="name" />
              </ReferenceField>
            </ShowField>
            <ShowField label={translate("resources.loads.fields.pickup_account_id")}>
              <ReferenceField source="pickup_account_id" reference="companies" link="show">
                <TextField source="name" />
              </ReferenceField>
            </ShowField>
            <ShowField label={translate("resources.loads.fields.delivery_account_id")}>
              <ReferenceField source="delivery_account_id" reference="companies" link="show">
                <TextField source="name" />
              </ReferenceField>
            </ShowField>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <h2 className="text-base font-semibold mb-4">Owners</h2>
          <Separator className="mb-4" />
          <div className="grid grid-cols-2 gap-6">
            <ShowField label={translate("resources.loads.fields.sales_owner_id")}>
              <ReferenceField source="sales_owner_id" reference="sales">
                <TextField source="last_name" />
              </ReferenceField>
            </ShowField>
            <ShowField label={translate("resources.loads.fields.purchasing_owner_id")}>
              <ReferenceField source="purchasing_owner_id" reference="sales">
                <TextField source="last_name" />
              </ReferenceField>
            </ShowField>
            <ShowField label={translate("resources.loads.fields.logistics_owner_id")}>
              <ReferenceField source="logistics_owner_id" reference="sales">
                <TextField source="last_name" />
              </ReferenceField>
            </ShowField>
            <ShowField label={translate("resources.loads.fields.invoiced_by_user_id")}>
              <ReferenceField source="invoiced_by_user_id" reference="sales">
                <TextField source="last_name" />
              </ReferenceField>
            </ShowField>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <h2 className="text-base font-semibold mb-4">Materials</h2>
          <Separator className="mb-4" />
          <InfiniteListBase
            resource="line_items"
            filter={{ load_id: record.id }}
            sort={{ field: "sort_order", order: "ASC" }}
            perPage={100}
            disableSyncWithLocation
            storeKey={false}
          >
            <MaterialsContent loadId={record.id} />
          </InfiniteListBase>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <h2 className="text-base font-semibold mb-4">Freight Details</h2>
          <Separator className="mb-4" />
          <div className="grid grid-cols-2 gap-6 mb-6">
            <ShowField label="Shipment Method">
              {SHIPMENT_METHOD_CHOICES.find(c => c.id === record.shipment_method)?.name ?? record.shipment_method ?? "—"}
            </ShowField>
            <ShowField label="Freight Terms">
              {FREIGHT_TERMS_CHOICES.find(c => c.id === record.freight_terms)?.name ?? record.freight_terms ?? "—"}
            </ShowField>
            <ShowField label="Origin">{record.origin_location ?? "—"}</ShowField>
            <ShowField label="Destination">{record.destination_location ?? "—"}</ShowField>
            <ShowField label="Container #">{record.container_number ?? "—"}</ShowField>
            <ShowField label="Booking #">{record.booking_number ?? "—"}</ShowField>
            <ShowField label="Bolt Seal Required">
              {record.bolt_seal_required ? "Yes" : "No"}
            </ShowField>
          </div>
          <Separator className="mb-4" />
          <h3 className="text-sm font-semibold mb-3">Freight Charges</h3>
          <InfiniteListBase
            resource="load_freight"
            filter={{ load_id: record.id }}
            sort={{ field: "created_at", order: "ASC" }}
            perPage={50}
            disableSyncWithLocation
            storeKey={false}
          >
            <FreightTable loadId={record.id} />
          </InfiniteListBase>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <h2 className="text-base font-semibold mb-4">Customs &amp; Broker Fees</h2>
          <Separator className="mb-4" />
          <InfiniteListBase
            resource="load_customs"
            filter={{ load_id: record.id }}
            sort={{ field: "created_at", order: "ASC" }}
            perPage={50}
            disableSyncWithLocation
            storeKey={false}
          >
            <CustomsTable loadId={record.id} />
          </InfiniteListBase>
        </CardContent>
      </Card>

      <FinancialsCard record={record} />

      {(record.supplier_requirements ||
        record.customer_requirements ||
        record.internal_notes ||
        record.notes_for_po ||
        record.notes_for_freight_forwarder) && (
        <Card>
          <CardContent className="pt-4">
            <h2 className="text-base font-semibold mb-4">Notes</h2>
            <Separator className="mb-4" />
            <div className="flex flex-col gap-4">
              {record.supplier_requirements && (
                <ShowField label={translate("resources.loads.fields.supplier_requirements")}>
                  <p className="whitespace-pre-line">{record.supplier_requirements}</p>
                </ShowField>
              )}
              {record.customer_requirements && (
                <ShowField label={translate("resources.loads.fields.customer_requirements")}>
                  <p className="whitespace-pre-line">{record.customer_requirements}</p>
                </ShowField>
              )}
              {record.internal_notes && (
                <ShowField label={translate("resources.loads.fields.internal_notes")}>
                  <p className="whitespace-pre-line">{record.internal_notes}</p>
                </ShowField>
              )}
              {record.notes_for_po && (
                <ShowField label={translate("resources.loads.fields.notes_for_po")}>
                  <p className="whitespace-pre-line">{record.notes_for_po}</p>
                </ShowField>
              )}
              {record.notes_for_freight_forwarder && (
                <ShowField label={translate("resources.loads.fields.notes_for_freight_forwarder")}>
                  <p className="whitespace-pre-line">{record.notes_for_freight_forwarder}</p>
                </ShowField>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <DocumentsPanel />

      <NotesPanel
        resource="load_notes"
        reference="loads"
        filter={{ load_id: record.id }}
        title="Activity"
        empty={<NoteCreate reference="loads" />}
      />
    </div>
  );
};

const fmt = (v: number | null | undefined) =>
  v != null ? Number(v).toFixed(2) : "—";

const FinancialsCard = ({ record }: { record: any }) => {
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refresh = useRefresh();

  const handleCalculate = async () => {
    setCalculating(true);
    setError(null);
    try {
      const { error: fnError } = await getSupabaseClient().functions.invoke(
        "calculate_load",
        { body: { load_id: record.id } },
      );
      if (fnError) throw fnError;
      refresh();
    } catch (err: any) {
      setError(err?.message ?? String(err));
    } finally {
      setCalculating(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold">Financials</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCalculate}
            disabled={calculating}
            className="gap-2"
          >
            <Calculator className="h-4 w-4" />
            {calculating ? "Calculating…" : "Calculate"}
          </Button>
        </div>
        {error && (
          <p className="text-sm text-destructive mb-4">{error}</p>
        )}
        <Separator className="mb-6" />
        <div className="grid grid-cols-2 gap-x-12 gap-y-6">
          <ShowField label="Vendor Currency">{record.vendor_currency ?? "—"}</ShowField>
          <ShowField label="Customer Currency">{record.customer_currency ?? "—"}</ShowField>
          <ShowField label="Vendor Invoice Total">{fmt(record.vendor_invoice_total)}</ShowField>
          <ShowField label="Customer Invoice Total">{fmt(record.customer_invoice_total)}</ShowField>
          <ShowField label="Vendor Payment Method">
            {PAYMENT_METHOD_CHOICES.find(c => c.id === record.vendor_payment_method)?.name ?? record.vendor_payment_method ?? "—"}
          </ShowField>
          <div />
          <ShowField label="Vendor Terms">
            {TERMS_CHOICES.find(c => c.id === record.vendor_terms)?.name ?? record.vendor_terms ?? "—"}
          </ShowField>
          <ShowField label="Customer Terms">
            {TERMS_CHOICES.find(c => c.id === record.customer_terms)?.name ?? record.customer_terms ?? "—"}
          </ShowField>
          <ShowField label="Vendor Date Paid">
            <DateField source="vendor_date_paid" />
          </ShowField>
          <ShowField label="Customer Date Paid">
            <DateField source="customer_date_paid" />
          </ShowField>
          <ShowField label="Vendor Total">{fmt(record.vendor_total)}</ShowField>
          <ShowField label="Customer Total">{fmt(record.customer_total)}</ShowField>
          <ShowField label="Vendor Total (USD)">{fmt(record.vendor_total_usd)}</ShowField>
          <ShowField label="Customer Total (USD)">{fmt(record.customer_total_usd)}</ShowField>
        </div>
        <div className="mt-4">
          <ShowField label="Zero Rated">
            {record.zero_rated ? "Yes" : "No"}
          </ShowField>
        </div>
        <Separator className="my-6" />
        <div className="grid grid-cols-3 gap-6">
          <ShowField label="Total Freight">{fmt(record.total_freight)}</ShowField>
          <ShowField label="Total Freight (w/ Markup)">{fmt(record.total_freight_markup)}</ShowField>
          <ShowField label="Total Customs">{fmt(record.total_customs)}</ShowField>
          <ShowField label="Profit (USD)">{fmt(record.profit_usd)}</ShowField>
          <ShowField label="Profit (CAD)">{fmt(record.profit_cad)}</ShowField>
          <ShowField label="Margin">{record.margin != null ? `${record.margin}%` : "—"}</ShowField>
        </div>
      </CardContent>
    </Card>
  );
};
